/**
 * First-run product tour state.
 *
 * The server stores a single timestamp on `users.onboarding_completed_at`. A
 * NULL value means the tour has never been completed (so the overlay should
 * show on next mount); a non-NULL value suppresses the tour. The "replay
 * tour" affordance on the security page resets the field to NULL.
 *
 * The shape returned by GET /api/v1/me already carries the field, so the
 * read path here just re-uses the same query key as `useAccountProfile()`
 * via a thin selector. The dismiss/reset mutations PATCH /api/v1/me/profile
 * with the appropriate value and invalidate the profile query.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../api'

export interface MeOnboarding {
  user_id: string
  primary_role: 'owner' | 'member' | 'actor' | 'supervisor' | null
  /** ISO timestamp when the user dismissed/finished the tour, or null. */
  onboarding_completed_at: string | null
}

interface MeResponse {
  id: string
  primary_role: 'owner' | 'member' | 'actor' | 'supervisor' | null
  onboarding_completed_at: string | null
}

export const onboardingKey = ['me', 'onboarding'] as const

/**
 * Read the current user's tour state. `data.onboarding_completed_at === null`
 * means the tour has not been completed and should be shown.
 */
export function useOnboardingState() {
  return useQuery({
    queryKey: onboardingKey,
    queryFn: async (): Promise<MeOnboarding> => {
      const me = await api<MeResponse>('/api/v1/me')
      return {
        user_id: me.id,
        primary_role: me.primary_role,
        onboarding_completed_at: me.onboarding_completed_at,
      }
    },
    // Tour state must reflect the current server-side
    // `users.onboarding_completed_at` on every fresh mount: dismissing the
    // tour on tab A and reloading tab B should not replay it. A short
    // staleTime + refetchOnMount make sure the read happens immediately
    // rather than serving an in-memory cache populated from a different
    // user/session.
    staleTime: 30 * 1000,
    refetchOnMount: 'always',
  })
}

/**
 * Mark the tour complete (called when the user clicks Done or Skip).
 * Sends `now` as an ISO string.
 */
export function useDismissTour() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (): Promise<void> => {
      await api('/api/v1/me/profile', {
        method: 'POST',
        body: JSON.stringify({ onboarding_completed_at: new Date().toISOString() }),
      })
    },
    onSuccess: () => {
      qc.setQueryData<MeOnboarding | undefined>(onboardingKey, (prev) =>
        prev ? { ...prev, onboarding_completed_at: new Date().toISOString() } : prev,
      )
    },
  })
}

/**
 * Reset the tour so it plays again on next mount. Used by the
 * "Replay onboarding tour" button on /account/security.
 */
export function useReplayTour() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (): Promise<void> => {
      await api('/api/v1/me/profile', {
        method: 'POST',
        body: JSON.stringify({ onboarding_completed_at: null }),
      })
    },
    onSuccess: () => {
      qc.setQueryData<MeOnboarding | undefined>(onboardingKey, (prev) =>
        prev ? { ...prev, onboarding_completed_at: null } : prev,
      )
      // Clear the dismiss flag so the tour re-mounts immediately. We migrated
      // from sessionStorage → localStorage in the wave 9 fix; clear both for
      // back-compat with users mid-session.
      try {
        localStorage.removeItem('ohear.tour-dismissed')
      } catch {
        // Ignore — localStorage may be unavailable in some embeds.
      }
      try {
        sessionStorage.removeItem('ohear.tour-dismissed')
      } catch {
        // Ignore — sessionStorage may be unavailable in some embeds.
      }
    },
  })
}
